import { useRegisterSW } from "virtual:pwa-register/react";
import {
  ActionIcon,
  Box,
  Burger,
  Button,
  Container,
  Flex,
  Header,
  MediaQuery,
  Menu,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import {
  IconBrandSupabase,
  IconChartBar,
  IconCoin,
  IconCoins,
  IconExternalLink,
  IconLogout,
  IconMoonStars,
  IconRefresh,
  IconRotateRectangle,
  IconSun,
  IconUser,
  IconUserCircle,
} from "@tabler/icons-react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { Logo } from "@/components/Logo";
import useAuthStore from "@/store/useAuthStore";
import { parseMoney } from "@/utils/helpers";

export default function DashboardHeader({ opened, toggleOpened }) {
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const handleLogout = useAuthStore((state) => state.handleLogout);
  const totalUserWithdrawPoints = useAuthStore((state) => state.totalUserWithdrawPoints);
  const updateUserTotalPoints = useAuthStore((state) => state.updateUserTotalPoints);
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const { data, mutate } = useSWR(["gift/balance"]);
  const remainCash = data?.total_cash || 0;

  async function handleRefetch() {
    await mutate();
    await updateUserTotalPoints();
    toast.success("캐시 잔액&고객 마일리지 합계 갱신 완료");
  }

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({});

  return (
    <Header height={60} p="xs">
      <Container fluid h="100%" px="xs">
        <Flex align="center" gap={10} h="100%">
          <MediaQuery largerThan="sm" styles={{ display: "none" }}>
            <Burger onClick={toggleOpened} opened={opened} size="sm" />
          </MediaQuery>
          <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
            <Link to="/">
              <Logo sx={{ height: 35, marginTop: -1 }} white={theme.colorScheme === "dark"} />
            </Link>
          </MediaQuery>

          <MediaQuery largerThan="sm" styles={{ display: "none" }}>
            <Link to="/">
              <Logo sx={{ height: 34 }} white={theme.colorScheme === "dark"} />
            </Link>
          </MediaQuery>
          <Box sx={{ flexGrow: 1 }} />

          {user.permission === "admin" && Boolean(data) && (
            <Menu position="bottom-end" shadow="md" transition="pop-top-right" width={230}>
              <Menu.Target>
                <Button
                  color={totalUserWithdrawPoints > remainCash ? "red" : "teal"}
                  variant={theme.colorScheme === "dark" ? "white" : "outline"}
                >
                  {parseMoney(remainCash)} 캐시
                </Button>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>마일리지 정보</Menu.Label>
                <Menu.Item icon={<IconCoins size={14} />}>
                  출금가능: <b>{parseMoney(totalUserWithdrawPoints)}원</b>
                </Menu.Item>
                <Menu.Label>비즈캐시 관리</Menu.Label>
                <Menu.Item icon={<IconRefresh size={14} />} onClick={handleRefetch}>
                  캐시/마일리지 합계 갱신
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item icon={<IconCoin size={14} />} onClick={() => navigate("/admin/points")}>
                  마일리지 내역 바로가기
                </Menu.Item>
                <Menu.Item
                  icon={<IconExternalLink size={14} />}
                  onClick={() => window.open("https://giftbiz.kakao.com/cash", "_blank")}
                >
                  비즈캐시 충전 바로가기
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}

          {user.permission !== "admin" && (
            <>
              <MediaQuery largerThan="sm" styles={{ display: "none" }}>
                <Button
                  onClick={() => navigate("/points")}
                  variant={theme.colorScheme === "dark" ? "white" : "outline"}
                >
                  {parseMoney(user.points || 0)}원
                </Button>
              </MediaQuery>
              <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
                <Button
                  onClick={() => navigate("/points")}
                  variant={theme.colorScheme === "dark" ? "white" : "outline"}
                >
                  마일리지: {parseMoney(user.points || 0)}원
                </Button>
              </MediaQuery>
            </>
          )}

          <Menu position="bottom" shadow="md" transition="pop-top-right" width={200}>
            <Menu.Target>
              <ActionIcon color="brand" size="lg" variant="filled">
                <IconUser size={18} />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>나의 계정</Menu.Label>
              <Menu.Item icon={<IconUserCircle size={14} />} onClick={() => navigate("/mypage")}>
                마이페이지
              </Menu.Item>

              <Menu.Item icon={<IconCoins size={14} />} onClick={() => navigate("/points")}>
                나의 마일리지
              </Menu.Item>

              <Menu.Item
                icon={<IconBrandSupabase size={14} />}
                onClick={() => navigate("/change-log")}
              >
                배차통 업데이트 로그
              </Menu.Item>

              <Menu.Item
                icon={colorScheme === "dark" ? <IconSun size={14} /> : <IconMoonStars size={14} />}
                onClick={() => toggleColorScheme()}
                sx={(t) => ({
                  color: t.colorScheme === "dark" ? t.colors.yellow[4] : t.colors.dark[6],
                })}
              >
                {colorScheme === "dark" ? "라이트 모드" : "다크 모드"}
              </Menu.Item>

              <Menu.Item
                icon={<IconRotateRectangle size={14} />}
                onClick={() => updateServiceWorker(true)}
                disabled={!needRefresh}
              >
                배차통 업데이트
              </Menu.Item>

              {(user.id === 1 || user.id === 5 || user.id === 87) && (
                <Menu.Item
                  icon={<IconChartBar size={14} />}
                  onClick={() => navigate("/admin/dispatch-trend")}
                >
                  배차 추이 분석
                </Menu.Item>
              )}

              <Menu.Divider />

              <Menu.Item color="red" icon={<IconLogout size={14} />} onClick={handleLogout}>
                로그아웃
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Flex>
      </Container>
    </Header>
  );
}
